import { fetchWithAuth } from "../utils/fetchWithAuth";
import productStrategyManifest from "../shared/featureWorkflows/productStrategyManifest";

class ProductStrategyWorkflowService {
  constructor() {
    this.WORKFLOW_STEPS = productStrategyManifest.steps.map(step => ({
      ...step,
      status: "pending",
    }));

    this.workflowResults = {};
  }

  getSteps() {
    return [...this.WORKFLOW_STEPS];
  }

  resetWorkflow() {
    this.workflowResults = {};
    this.WORKFLOW_STEPS.forEach((step) => {
      step.status = "pending";
    });
  }

  async executeWorkflow(params, callbacks) {
    const { designChallenge, briefData, planId, enabledFeatures = [] } = params;

    const { onStepStart, onStepComplete, onComplete, onError, onInsufficientCredits } = callbacks;

    // Check credits before starting (strategy is charged per section = 2 credits; full strategy ≈ strategy-section)
    try {
      const creditCheck = await fetchWithAuth('/api/credits/check?action=strategy-section');
      if (creditCheck.ok) {
        const creditData = await creditCheck.json();
        if (!creditData.allowed) {
          if (onInsufficientCredits) onInsufficientCredits();
          else if (onError) onError(Object.assign(new Error('Insufficient credits'), { type: 'insufficient_credits' }));
          return null;
        }
      }
    } catch (_) {}

    console.log("🔬 PRODUCT STRATEGY WORKFLOW: Starting", {
      designChallenge: designChallenge?.substring(0, 100) + "...",
      hasBriefData: !!briefData,
      planId,
      enabledFeatures,
    });
    
    // Store enabled features for use in workflow steps
    this.enabledFeatures = enabledFeatures;

    try {
      const steps = this.getSteps();

      if (onStepStart) onStepStart(0, { ...steps[0], agent: "Product Lead" });

      const deconstructionResults = await this.executeBriefDeconstruction(
        designChallenge,
        briefData,
        planId,
      );
      console.log("📝 Brief Deconstruction completed");
      if (onStepComplete)
        onStepComplete(
          0,
          { ...steps[0], status: "completed" },
          deconstructionResults,
        );

      if (onStepStart) onStepStart(1, { ...steps[1], agent: "Product Lead" });

      let marketScanResults = null;
      try {
        marketScanResults = await this.executeMarketScan(
          designChallenge,
          briefData,
          deconstructionResults,
        );
        console.log("📝 Audience & Product Pulse completed");
      } catch (scanError) {
        console.warn("⚠️ Market scan failed, continuing without market context:", scanError.message);
        marketScanResults = null;
      }
      if (onStepComplete)
        onStepComplete(
          1,
          { ...steps[1], status: "completed" },
          marketScanResults,
        );

      if (onStepStart) onStepStart(2, { ...steps[2], agent: "Product Lead" });

      const hypothesisResults = await this.executeDesignHypothesis(
        designChallenge,
        briefData,
        deconstructionResults,
        marketScanResults,
        planId,
      );
      console.log("📝 Design Hypothesis completed");
      if (onStepComplete)
        onStepComplete(
          2,
          { ...steps[2], status: "completed" },
          hypothesisResults,
        );

      const finalReport = {
        briefDeconstruction: deconstructionResults,
        informationAssessment: hypothesisResults.informationAssessment,
        strategicGuidance: hypothesisResults.strategicGuidance,
        audienceInsights: hypothesisResults.audienceInsights,
        designHypotheses: hypothesisResults.designHypotheses,
        designHypothesis: hypothesisResults.designHypotheses,
        customerGoals: hypothesisResults.customerGoals,
        painPoints: hypothesisResults.painPoints,
        unmetNeeds: hypothesisResults.unmetNeeds,
        designVision: hypothesisResults.designVision,
        competitivePositioning: hypothesisResults.competitivePositioning,
        competitiveUxPositioning: hypothesisResults.competitivePositioning,
        experienceDesignPrinciples:
          hypothesisResults.experienceDesignPrinciples,
        successMetrics: hypothesisResults.successMetrics,
        strategicThemes: hypothesisResults.strategicThemes,
        opportunityAreas: hypothesisResults.opportunityAreas,
        strategicDeliverables: hypothesisResults.strategicDeliverables,
        metadata: {
          generatedAt: new Date().toISOString(),
        },
      };

      console.log("🔥🔥🔥 PRODUCT STRATEGY WORKFLOW: About to call onComplete callback");
      if (onComplete) {
        console.log("🔥🔥🔥 PRODUCT STRATEGY WORKFLOW: onComplete exists, calling it now");
        onComplete(finalReport);
        console.log("🔥🔥🔥 PRODUCT STRATEGY WORKFLOW: onComplete callback completed");
      } else {
        console.log("🔥🔥🔥 PRODUCT STRATEGY WORKFLOW: onComplete is MISSING!");
      }

      // Deduct credits after successful completion
      try {
        const deductRes = await fetchWithAuth('/api/credits/deduct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'strategy-section' }),
        });
        if (deductRes.ok) {
          const deductData = await deductRes.json();
          if (!deductData.success && onInsufficientCredits) onInsufficientCredits();
        }
      } catch (_) {}

      console.log("🔥🔥🔥 PRODUCT STRATEGY WORKFLOW: About to return finalReport");
      return finalReport;
    } catch (error) {
      console.error("❌ Product Strategy workflow failed:", error);
      if (onError) onError(error);
      throw error;
    }
  }

  async executeBriefDeconstruction(designChallenge, briefData, planId = null) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/product-strategy-brief-deconstruction",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            brief_data: briefData,
            plan_id: planId,
            agentId: "1",
            context: {
              source: "product_strategy_workflow",
              step: "brief_deconstruction",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(
          `Brief deconstruction failed with status ${result.status}`,
        );
      }

      const data = await result.json();
      this.workflowResults.briefDeconstruction = data;
      return data;
    } catch (error) {
      console.error("Brief deconstruction step failed:", error);
      throw error;
    }
  }

  async executeMarketScan(designChallenge, briefData, deconstruction) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/product-strategy-market-scan",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            brief_data: briefData,
            brief_deconstruction: deconstruction,
            agentId: "1",
            context: {
              source: "product_strategy_workflow",
              step: "market_scan",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(
          `Market scan failed with status ${result.status}`,
        );
      }

      const data = await result.json();
      this.workflowResults.marketScan = data;
      return data;
    } catch (error) {
      console.error("Market scan step failed:", error);
      throw error;
    }
  }

  async executeDesignHypothesis(designChallenge, briefData, deconstruction, marketContext = null, planId = null) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/product-strategy-design-hypothesis",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            brief_data: briefData,
            brief_deconstruction: deconstruction,
            market_context: marketContext,
            enabled_features: this.enabledFeatures || [],
            plan_id: planId,
            agentId: "1",
            context: {
              source: "product_strategy_workflow",
              step: "design_hypothesis",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(
          `Design hypothesis failed with status ${result.status}`,
        );
      }

      const data = await result.json();
      this.workflowResults.designHypothesis = data;
      return data;
    } catch (error) {
      console.error("Design hypothesis step failed:", error);
      throw error;
    }
  }

  async saveReport(planId, planName, reportData) {
    try {
      const result = await fetchWithAuth("/api/product-strategy/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId,
          planName,
          designChallenge: reportData.designChallenge,
          briefDeconstruction: reportData.briefDeconstruction,
          informationAssessment: reportData.informationAssessment,
          strategicGuidance: reportData.strategicGuidance,
          audienceInsights: reportData.audienceInsights,
          designHypotheses: reportData.designHypotheses,
          designHypothesis: reportData.designHypothesis,
          customerGoals: reportData.customerGoals,
          painPoints: reportData.painPoints,
          unmetNeeds: reportData.unmetNeeds,
          designVision: reportData.designVision,
          competitivePositioning: reportData.competitivePositioning,
          competitiveUxPositioning: reportData.competitiveUxPositioning,
          experienceDesignPrinciples: reportData.experienceDesignPrinciples,
          successMetrics: reportData.successMetrics,
          strategicThemes: reportData.strategicThemes,
          opportunityAreas: reportData.opportunityAreas,
          strategicDeliverables: reportData.strategicDeliverables,
        }),
      });

      if (!result.ok) {
        throw new Error(`Save report failed with status ${result.status}`);
      }

      const data = await result.json();
      console.log("✅ Product strategy report saved:", data);
      return data;
    } catch (error) {
      console.error("Failed to save product strategy report:", error);
      throw error;
    }
  }

  async loadLatestReport(planId) {
    try {
      const result = await fetchWithAuth(
        `/api/product-strategy/plan/${planId}/latest`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Load report failed with status ${result.status}`);
      }

      const data = await result.json();
      if (data.report) {
        this.workflowResults = {
          briefDeconstruction: data.report.brief_deconstruction,
          informationAssessment: data.report.information_assessment,
          strategicGuidance: data.report.strategic_guidance,
          audienceInsights: data.report.audience_insights,
          designHypotheses: data.report.design_hypothesis,
          designHypothesis: data.report.design_hypothesis,
          customerGoals: data.report.customer_goals,
          painPoints: data.report.pain_points,
          unmetNeeds: data.report.unmet_needs,
          designVision: data.report.design_vision,
          competitivePositioning: data.report.competitive_ux_positioning,
          competitiveUxPositioning: data.report.competitive_ux_positioning,
          experienceDesignPrinciples: data.report.experience_design_principles,
          successMetrics: data.report.success_metrics,
          strategicThemes: data.report.strategic_themes,
          opportunityAreas: data.report.opportunity_areas,
          strategicDeliverables: data.report.strategic_deliverables,
        };
      }
      return data;
    } catch (error) {
      console.error("Failed to load product strategy report:", error);
      throw error;
    }
  }

  async loadReportHistory(planId) {
    try {
      const result = await fetchWithAuth(
        `/api/product-strategy/plan/${planId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Load history failed with status ${result.status}`);
      }

      const data = await result.json();
      return data.reports || [];
    } catch (error) {
      console.error("Failed to load product strategy report history:", error);
      throw error;
    }
  }

  async loadReport(reportId) {
    try {
      const result = await fetchWithAuth(`/api/product-strategy/${reportId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!result.ok) {
        throw new Error(`Load report failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults = {
        briefDeconstruction: data.brief_deconstruction,
        informationAssessment: data.information_assessment,
        strategicGuidance: data.strategic_guidance,
        audienceInsights: data.audience_insights,
        designHypotheses: data.design_hypothesis,
        designHypothesis: data.design_hypothesis,
        customerGoals: data.customer_goals,
        painPoints: data.pain_points,
        unmetNeeds: data.unmet_needs,
        designVision: data.design_vision,
        competitivePositioning: data.competitive_ux_positioning,
        competitiveUxPositioning: data.competitive_ux_positioning,
        experienceDesignPrinciples: data.experience_design_principles,
        successMetrics: data.success_metrics,
        strategicThemes: data.strategic_themes,
        opportunityAreas: data.opportunity_areas,
        strategicDeliverables: data.strategic_deliverables,
      };
      return data;
    } catch (error) {
      console.error("Failed to load product strategy report:", error);
      throw error;
    }
  }

  async deleteReport(reportId) {
    try {
      const result = await fetchWithAuth(`/api/product-strategy/${reportId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!result.ok) {
        throw new Error(`Delete report failed with status ${result.status}`);
      }

      const data = await result.json();
      console.log("✅ Product strategy report deleted:", data);
      return data;
    } catch (error) {
      console.error("Failed to delete product strategy report:", error);
      throw error;
    }
  }
}

const productStrategyWorkflowService = new ProductStrategyWorkflowService();
export default productStrategyWorkflowService;
